<template>
  <DataTable
    ref="dataTable"
    tableStyle="min-width: 50rem"
    table-class="table-colleges"
    :value="shownColleges"
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    sortField="institutionName"
    :sortOrder="1"
    paginator
    scrollable
    scrollHeight="flex"
    @page="onPage"
  >
    <Column field="institutionName" sortable header="Institution" style="width: 25ch">
      <template #body="slotProps">
        <span class="text-sm">{{ slotProps.data.institutionName }}</span>
      </template>
    </Column>
    <Column field="state" sortable header="State" style="width:12ch;">
      <template #body="slotProps">
        <span class="text-sm">{{ states.find((el) => el.code == slotProps.data.state)?.name }}</span>
      </template>
    </Column>
    <Column field="level" sortable header="Level" style="width:12ch;">
      <template #body="slotProps">
        <span class="text-sm">{{ levels.find((el) => el.code == slotProps.data.level)?.name }}</span>
      </template>
    </Column>
    <Column field="control" style="width: 13ch;" sortable header="Control">
      <template #body="slotProps">
        <span class="text-sm">{{ control?.find((el) => el.code == slotProps.data.control)?.name }}</span>
      </template>
    </Column>
    <Column field="academicAssociation" sortable header="Classification" style="width:16ch;">
      <template #body="slotProps">
        <span class="text-sm">{{ classificationLabel(slotProps.data.academicAssociation) }}</span>
      </template>
    </Column>

    <!-- Catalog column: only present when this view is allowed to open
         catalogs (CurriculumFinder). When `allowCatalogs` is false (the
         public CoverageReport), the table is a coverage list with no catalog
         access, no viewer, and no login. -->
    <Column v-if="allowCatalogs" header="Catalog" style="width: 20ch;">
      <template #body="slotProps">
        <Dropdown
          :options="catalogs[slotProps.data.id] || []"
          optionLabel="edition"
          placeholder="Select a catalog edition"
          variant="link-list"
          class="w-full"
          @before-show="onClickCatalogs(slotProps.data.id)"
          @change="onCatalogPicked($event, slotProps.data)"
        >
          <template #option="optProps">
            <span class="py-3 px-5 block flex items-center gap-2">
              {{ optProps.option.edition }}
              <svg
                v-if="optProps.option.format === 'Web'"
                class="catalog-opt__external"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </span>
          </template>
          <template #empty>
            <div class="py-3 px-5 text-sm">
              {{ catalogs[slotProps.data.id] === undefined ? 'Loading catalogs...' : 'No catalogs found' }}
            </div>
          </template>
        </Dropdown>
      </template>
    </Column>

    <template #empty>
      <p class="text-left text-sm font-bold py-6">
        <template v-if="isLoading">
          <svg class="animate-spin inline -ml-1 mr-1 h-4 w-4 text-mute" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading colleges...
        </template>
        <template v-else-if="!filters.state">
          Select a state to begin.
        </template>
        <template v-else>
          No results found
        </template>
      </p>
    </template>
  </DataTable>
</template>

<script>
import { states, levels, control, classificationLabel } from '@/data/filterOptions';

const catalogCacheKey = (institutionId) => `college-search--catalog-cache--${institutionId}`;

export default {
  name: 'CollegeTable',

  props: {
    // Flat filter model owned by the parent: { state, level, control }.
    // Selecting a state triggers the API fetch; the rest filter rows in `shownColleges`.
    filters: {
      type: Object,
      required: true,
    },
    // When true (CurriculumFinder), each row exposes its catalog editions and
    // picking one emits `select-catalog` so the parent opens it in the in-app
    // viewer. When false (public CoverageReport), the catalog column is hidden
    // entirely — the table is a coverage list only.
    allowCatalogs: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['select-catalog'],

  data() {
    return {
      states,
      levels,
      control,
      catalogs: {},
      colleges: [],
      isLoading: false,
    };
  },

  created() {
    // The parent may already hold a chosen state when this table mounts
    // (e.g. returning to results after viewing a catalog). The state watcher
    // only fires on change, so fetch here too if a state is already set.
    if (this.filters.state) {
      this.fetchStateColleges(this.filters.state);
    }
  },

  computed: {
    shownColleges() {
      // No data is loaded until a state is chosen.
      if (!this.filters.state) {
        return [];
      }

      // Every active filter must match (logical AND).
      return this.colleges.filter((college) => {
        if (this.filters.level && college.level !== this.filters.level) {
          return false;
        }

        if (this.filters.control && college.control !== this.filters.control) {
          return false;
        }

        return true;
      });
    },
  },

  watch: {
    // Analytics payloads are deliberately FLAT. GA4 event parameters must be
    // scalars — a nested object is rendered as the string "[object Object]", so
    // the previous `filter: { type, value }` shape reached GA4 carrying nothing
    // usable. Verified by reading live /g/collect payloads on production.
    'filters.state'(newState) {
      if (newState) {
        this.fetchStateColleges(newState);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'filter_by_state',
          filter_type: 'state',
          filter_value: newState,
        });
      } else {
        this.colleges = [];
        this.catalogs = {};
      }
    },

    'filters.level'(newLevel) {
      if (newLevel) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'filter_by_level',
          filter_type: 'level',
          filter_value: newLevel,
        });
      }
    },

    'filters.control'(newControl) {
      if (newControl) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'filter_by_control',
          filter_type: 'control',
          filter_value: newControl,
        });
      }
    },

  },

  methods: {
    classificationLabel,

    async fetchStateColleges(state) {
      this.isLoading = true;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/institutions/state?id=${state}`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.VUE_APP_API_KEY,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        this.colleges = data.message || [];
      } catch (error) {
        console.error('Error fetching state institutions:', error);
        this.colleges = [];
      } finally {
        this.isLoading = false;
      }
    },

    async getCatalogs(institutionId) {
      if (this.catalogs[institutionId]) {
        return this.catalogs[institutionId];
      }

      try {
        const cached = JSON.parse(localStorage.getItem(catalogCacheKey(institutionId)) ?? '[]');
        if (Array.isArray(cached) && cached.length) {
          this.catalogs[institutionId] = cached;
          return cached;
        }
      } catch (error) {
        console.error('Error reading cached catalogs:', error);
      }

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/catalogs/institution?id=${institutionId}`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.VUE_APP_API_KEY,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        const json = await response.json();
        const data = (json.message || []).sort((a, b) => parseInt(b.edition, 10) - parseInt(a.edition, 10));
        localStorage.setItem(catalogCacheKey(institutionId), JSON.stringify(data));
        this.catalogs[institutionId] = data;
        return data;
      } catch (error) {
        console.error('Error fetching catalogs:', error);
        return [];
      }
    },

    async onClickCatalogs(institutionId) {
      const catalogs = await this.getCatalogs(institutionId);
      this.catalogs[institutionId] = catalogs;
    },

    onCatalogPicked(event, institution) {
      const catalog = event.value;
      if (!catalog) return;

      // Flat, for the same reason as the filter events above: GA4 would have
      // rendered this whole nested `catalog` object as "[object Object]". This
      // is the "what are people actually opening" event, so it is the one that
      // most needs its parameters to survive.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'catalog_selected',
        institutionName: institution.institutionName,
        institutionId: institution.id,
        catalogId: catalog.id,
        edition: catalog.edition,
      });

      this.$emit('select-catalog', { catalog, institution });
    },

    // When the user pages, the scroll body keeps its previous offset, leaving
    // them mid-table on the new page. Reset it back to the first row.
    onPage() {
      this.$nextTick(() => {
        const root = this.$refs.dataTable?.$el;
        if (!root) return;
        const scrollBody = root.querySelector('[data-pc-section="wrapper"]');
        scrollBody?.scrollTo({ top: 0, behavior: 'smooth' });
        root.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    },
  },
};
</script>

<style scoped>
/* The datatable preset sets the inner <table> to h-full. Once the scroll body
   has a bounded height, that stretches a short result set to fill it and
   spreads the rows apart. Pin the table to its natural height instead. */
:deep(table.table-colleges) {
  height: auto;
}

.catalog-opt__external {
  width: 12px;
  height: 12px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.7;
}
</style>
